import type { LinearIssueOption, RepoPullRequestOption, TaskStart } from "@rivets/core";

export type TaskStartTab = "pullRequests" | "branches" | "issues";

/** What the new-task picker holds: the wire start plus what its trigger draws. */
export type TaskStartSelection =
  | { kind: "pullRequest"; pullRequest: RepoPullRequestOption }
  | { kind: "branch"; branch: string }
  | { kind: "linearIssue"; issue: LinearIssueOption };

export function taskStartRequest(selection: TaskStartSelection | undefined): TaskStart | undefined {
  if (!selection) return undefined;
  switch (selection.kind) {
    case "pullRequest": return { kind: "pullRequest", number: selection.pullRequest.number };
    case "branch": return { kind: "branch", branch: selection.branch };
    case "linearIssue": return { kind: "linearIssue", issueId: selection.issue.id };
  }
}

export function taskStartLabel(selection: TaskStartSelection): string {
  switch (selection.kind) {
    case "pullRequest": return `#${selection.pullRequest.number} ${selection.pullRequest.title}`;
    case "branch": return selection.branch;
    case "linearIssue": return `${selection.issue.identifier} ${selection.issue.title}`;
  }
}

/** An issue carries its own content to the agent, so the prompt may be left empty. */
export function taskStartAllowsEmptyPrompt(selection: TaskStartSelection | undefined): boolean {
  return selection?.kind === "linearIssue";
}

/** Every whitespace-separated term must match somewhere, like `MenuSelect`. */
export function matchesTerms(haystack: readonly (string | null | undefined)[], search: string): boolean {
  const terms = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (terms.length === 0) return true;
  const text = haystack.filter(Boolean).join(" ").toLowerCase();
  return terms.every((term) => text.includes(term));
}

/** The default branch is not offered: starting "on" it is just a new task. */
export function pickableBranches(branches: readonly string[], defaultBranch: string | undefined, search: string): string[] {
  return branches.filter((branch) => branch !== defaultBranch && matchesTerms([branch], search));
}

export function pickablePullRequests(pullRequests: readonly RepoPullRequestOption[], search: string): RepoPullRequestOption[] {
  const needle = search.trim().replace(/^#/, "");
  return pullRequests.filter((pull) =>
    matchesTerms([`#${pull.number}`, String(pull.number), pull.title, pull.headBranch, pull.author], needle));
}
